import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Wrench, 
  Droplets, 
  Flame, 
  ShowerHead, 
  CheckCircle2, 
  Clock, 
  Shield, 
  Award,
  ArrowRight,
  Phone
} from 'lucide-react';

export default function HomePage() {
  const features = [
    'Over 20 Years Experience',
    'Certified Engineers',
    'Borough Approved'
  ];

  const services = [
    {
      icon: Wrench,
      title: 'Professional Plumbing',
      description: 'Expert plumbing solutions for residential and commercial properties across London.',
      color: 'text-primary'
    },
    {
      icon: Droplets,
      title: 'Leak Detection & Repairs',
      description: 'Advanced leak detection technology and professional repairs to protect your property.',
      color: 'text-primary'
    },
    {
      icon: ShowerHead,
      title: 'Bathroom & Kitchen Installations',
      description: 'Complete bathroom and kitchen design, installation, and renovation services with meticulous attention to detail.',
      color: 'text-primary'
    },
    {
      icon: Flame,
      title: 'Boiler & Heating Services',
      description: 'Installation, maintenance, and repair of boilers and heating systems by qualified engineers.',
      color: 'text-primary'
    }
  ];

  const stats = [
    { icon: Clock, value: '20+', label: 'Years Experience' },
    { icon: Award, value: '10+', label: 'Years with Boroughs' },
    { icon: Shield, value: '100%', label: 'Safety Certified' },
    { icon: CheckCircle2, value: '5000+', label: 'Homes Improved' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85"
            alt="Professional plumbing services background"
            className="w-full h-full object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/95 via-secondary/85 to-secondary/70" />
          {/* Accent Gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-slide-up text-secondary-foreground">
              <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors duration-300">
                ✨ Trusted Plumbing Experts Since 2005
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-secondary-foreground leading-tight">
                Making London Homes{' '}
                <span className="text-primary">Safe & Suitable</span>{' '}
                for Everyone
              </h1>
              
              <p className="text-lg text-secondary-foreground/90 leading-relaxed">
                With over 20 years of experience and a decade working with London Boroughs and councils, 
                we deliver professional plumbing services that prioritize safety and quality.
              </p>

              {/* Features List */}
              <div className="flex flex-wrap gap-4">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg glass border border-secondary-foreground/20"
                  >
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium text-secondary-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow transition-all duration-300 group"
                >
                  <Link to="/contact">
                    Get a Quote
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </Button>
                <Button 
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-secondary-foreground/20 hover:bg-secondary-foreground/10 transition-all duration-300"
                >
                  <a href="tel:07356255155" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Us
                  </a>
                </Button>
              </div>
            </div>

            {/* Right Content - Stats Card */}
            <div className="relative lg:h-[500px] h-[350px] animate-fade-in hidden lg:flex items-end justify-center">
              {/* Floating Stats Card */}
              <div className="glass rounded-2xl p-8 shadow-xl backdrop-blur-lg border border-secondary-foreground/20 w-full max-w-md">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-primary mb-2">5000+</p>
                    <p className="text-sm text-secondary-foreground/80">Homes Improved</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-primary mb-2">4.9★</p>
                    <p className="text-sm text-secondary-foreground/80">Customer Rating</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-primary mb-2">20+</p>
                    <p className="text-sm text-secondary-foreground/80">Years Experience</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-primary mb-2">10+</p>
                    <p className="text-sm text-secondary-foreground/80">Years with Boroughs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-secondary text-secondary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center space-y-3 group">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <p className="text-3xl md:text-4xl font-bold font-heading">{stat.value}</p>
                    <p className="text-sm text-secondary-foreground/80 mt-1">{stat.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Our Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground">
              Expert Plumbing Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive plumbing solutions for residential and commercial properties
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index}
                  className="p-6 border-2 hover:border-primary/50 hover:shadow-lg transition-all duration-300 group cursor-pointer bg-card"
                >
                  <div className="space-y-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                      <Icon className={`w-6 h-6 ${service.color}`} />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-2 hover:bg-muted group"
            >
              <Link to="/about">
                View All Services
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Work Gallery Section */}
      <section className="py-20 md:py-32 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Our Work
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground">
              Quality Craftsmanship
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See examples of our professional plumbing installations and renovations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                src: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85",
                title: "Modern Bathroom Installation",
                category: "Bathroom"
              },
              {
                src: "https://images.unsplash.com/photo-1629079447777-1e605162dc8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85",
                title: "Luxury Kitchen & Bathroom",
                category: "Kitchen & Bath"
              },
              {
                src: "https://images.unsplash.com/photo-1768321916212-17ae334a3d63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxib2lsZXIlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzY5ODE5Nzk2fDA&ixlib=rb-4.1.0&q=85",
                title: "Boiler & Heating Installation",
                category: "Heating"
              },
              {
                src: "https://images.unsplash.com/photo-1769763828411-eb09bb05d97f",
                title: "Modern Kitchen Tap Installation",
                category: "Kitchen"
              },
              {
                src: "https://images.unsplash.com/photo-1760571327612-8ab776dcd462?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxwbHVtYmluZyUyMHJlcGFpcnxlbnwwfHx8fDE3Njk4MTk4MDl8MA&ixlib=rb-4.1.0&q=85",
                title: "Professional Pipework",
                category: "Installation"
              },
              {
                src: "https://images.unsplash.com/photo-1521783593447-5702b9bfd267?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85",
                title: "Complete Kitchen & Bath Suite",
                category: "Installation"
              },
              {
                src: "https://images.pexels.com/photos/7859953/pexels-photo-7859953.jpeg",
                title: "Expert Repairs",
                category: "Repair"
              }
            ].map((item, index) => (
              <Card 
                key={index}
                className="group overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <Badge className="bg-primary/90 text-primary-foreground mb-2">
                      {item.category}
                    </Badge>
                    <h3 className="text-lg font-heading font-semibold text-white">
                      {item.title}
                    </h3>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-secondary text-secondary-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold">
              Ready to Improve Your Property?
            </h2>
            <p className="text-lg text-secondary-foreground/80 max-w-2xl mx-auto">
              Work with London's trusted plumbing experts. Over 20 years of experience 
              making homes safe and suitable for residents across all boroughs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow"
              >
                <a href="tel:07356255155" className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call 07356 255155
                </a>
              </Button>
              <Button 
                asChild
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-secondary-foreground/20 hover:bg-secondary-foreground/10"
              >
                <Link to="/contact">Request a Quote</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}